import React from "react";
import NumericStepper from "src/@components/input.numeric.stepper";
import { ApplicationContext } from "src/context";
import { getLanguageField } from "src/utils/language";

type GuestSelectorProps = {
    guests?: Array<GuestSelectorValue>;
    ageCategories: Array<TAgeCategory>;
    onChange: (guests: Array<GuestSelectorValue>) => void;
}

export default class GuestSelector extends React.Component<GuestSelectorProps> {

    /** @ts-ignore-next */
    public context!: React.ContextType<typeof ApplicationContext>;
    static contextType = ApplicationContext;

    static getDefaultValue(ageCategories: Array<TAgeCategory>, overrides?: Record<string, number>): Array<GuestSelectorValue> {
        const guests = [];

        for (const cat of ageCategories) {
            for (let index = 0; index < cat.min_amount; index++) {
                guests.push({ type: cat.type, age: Math.round(((cat.min_age || 1) + (cat.max_age || 99)) / 2) });
                if (overrides && overrides[cat.type]) {
                    for (let i = 0; i < overrides[cat.type]; i++) { guests.push({ type: cat.type, age: Math.round(((cat.min_age || 1) + (cat.max_age || 99)) / 2) }); }
                }
            }

        }
        
        return guests;
    }

    static getHumanReadableQuantities(guests: Array<GuestSelectorValue> = [], ageCategories: TAgeCategory[], context: React.ContextType<typeof ApplicationContext>): string {
        const summ: Record<string, number> = {};

        for (const guest of guests) {
            summ[guest.type] = summ[guest.type] || 0;
            summ[guest.type] += 1;
        }

        const parts = [];
        for (const agecat of ageCategories) {
            if (summ[agecat.type] > 0) {
                if (summ[agecat.type] === 1) parts.push(`1 ${context.translate(agecat.translations, 'name')}`.toLocaleLowerCase());
                else parts.push(`${summ[agecat.type]} ${context.translate(agecat.translations, 'plural')}`.toLocaleLowerCase());
            }
        }
        
        return parts.join(", ");
    }

    getAgeDisplay = (max_age?: number, min_age?: number) => {
        if (min_age && max_age) return `${min_age} - ${max_age}`;
        if (min_age && !max_age) return `+ ${min_age}`;
        if (!min_age && max_age) return `0 - ${max_age}`;
    }

    updateGuestQuantity = (ageCategory: TAgeCategory, quantity: number) => {
        const guests = [...(this.props.guests || [])];

        const { type, min_age, max_age } = ageCategory;
    
        // Filter guests of the specified type
        const guestsOfType = guests.filter(guest => guest.type === type);
 
        if (guestsOfType.length < quantity) {
            // Add guests until the desired quantity is reached
            const guestsToAdd = quantity - guestsOfType.length;

            for (let i = 0; i < guestsToAdd; i++) {
                guests.push({
                    type,
                    age: Math.round(((min_age || 1) + (max_age || 99)) / 2)
                });
            }
        } else {
            // Remove the last guest of the specified type
            const lastGuestIndex = guests.map(guest => guest.type).lastIndexOf(type);
            if (lastGuestIndex !== -1) {
                guests.splice(lastGuestIndex, 1);
            }
        }

        if (this.props.onChange) this.props.onChange(guests);
    }

    updateGuestAge = (index: number, age: number) => {
        const guests = [...(this.props.guests || [])];
        guests[index].age = age;
        if (this.props.onChange) this.props.onChange(guests);
    }

    getGuestTypeIndex = (index: number) => {
        const guests = [...(this.props.guests || [])];
        const currentObj = guests[index];
        let count = 0;
  
        for (let i = 0; i < index; i++) {
            const obj = guests[i];
            if (obj.type === currentObj.type) {
                count++;
            }
        }

        return count + 1;
    }

    checkIfAtLeastOneGuestNeedsToSelectAge = () => {
        const guests = [...(this.props.guests || [])];
      
        return !!guests.find(guest => {
            const guestInAgeCategories = this.props.ageCategories.find(agecat => agecat.type === guest.type);
            if (guestInAgeCategories && guestInAgeCategories.age_selection) return true;
            return false;
        });
    }

    getNumberOfGuestsForCategoryType(type: TAgeCategoryType): number {
        const guests = this.props.guests || [];
        return guests.filter(guest => guest.type === type).length;
    }  

    render(){
        const { getAgeDisplay, getGuestTypeIndex, checkIfAtLeastOneGuestNeedsToSelectAge } = this;
        const { ageCategories, guests = [] } = this.props;
        const language = this.context.language;
        
        return (
        <div className="flex flex-col w-full">
            {/* guest age category selectors */}
            <div className="flex flex-col gap-4 w-full">
            {
            ageCategories.map((ageCategory, index) => {
                const { max_age, min_age, max_amount, min_amount, translations, type } = ageCategory;
                return (
                <div key={`guest_${index}`} className="flex justify-between">
                    <span>{getLanguageField(translations, language, "name")} <span className="text-[#A6A6AE]">({getAgeDisplay(max_age || 99, min_age || 1)})</span></span>
                    <NumericStepper
                        layout="rounded"
                        value={this.getNumberOfGuestsForCategoryType(type)} min={min_amount} max={max_amount}
                        onChange={(value)=> { this.updateGuestQuantity(ageCategory, value) }}
                    />
                </div>
                )
            })
            }
            </div>
            {/* guest age selector */}
            {
            checkIfAtLeastOneGuestNeedsToSelectAge()
            ? 
                <div className="flex flex-col gap-4 w-full pt-4 mt-4 border-t-[1px]">
                <div className="flex justify-between">
                    <span> {this.context.literal('guestpicker_ageselection_title')} <span className="text-[#A6A6AE]">({this.context.literal('guestpicker_ageselection_description')})</span></span>
                </div>
                {
                    guests.map((guest, index) => {
                        const ageCategoryOfThisGuest = ageCategories.find(category => category.type === guest.type);
                        if (!ageCategoryOfThisGuest) return null;
                        if (!ageCategoryOfThisGuest.age_selection) return null;
                    
                        const { translations, max_age, min_age } = ageCategoryOfThisGuest;
                        return (
                            <div key={`ageselection-${index}`} className="flex justify-between items-center">
                                <span>{getLanguageField(translations, language, "name")} {getGuestTypeIndex(index)} {this.context.literal('guestpicker_age')}</span>
                                <div className="flex items-center gap-2">
                                    <NumericStepper
                                        layout="rounded"
                                        value={guest.age}
                                        min={min_age}
                                        max={max_age || min_age}
                                        onChange={(value)=> { this.updateGuestAge(index, value) }}
                                    />
                                </div>
                            </div>
                        )
                    })
                }
                </div>
            : null
            }
            
        </div>
        )
    }
}